import { AlertTriangle, Chrome, HardDrive, MonitorDown, RefreshCcw, ShieldCheck, Smartphone } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
        {n}
      </div>
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <div className="mt-1 text-sm leading-relaxed text-slate-600">{children}</div>
      </div>
    </li>
  );
}

function Card({ icon: Icon, title, children }: { icon: typeof Chrome; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon size={20} className="text-indigo-600" />
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function DocsChromeErrorCode5() {
  useMetaTags({
    title: "Fix Chrome Aw, Snap Error Code 5 in OmniCanvas",
    description:
      "How to recover when OmniCanvas shows Chrome's Aw, Snap page or Error code 5, without deleting your notes unless absolutely necessary.",
    url: "https://omnicanvasnotes.com/docs/troubleshooting-chrome-error-code-5",
  });

  return (
    <div className="pt-28 pb-20">
      <div className="mx-auto max-w-3xl px-6">
        <div className="mb-12 text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
            Troubleshooting
          </p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-slate-900">
            Fix Chrome Aw, Snap Error Code 5
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-slate-600">
            If OmniCanvas opens in Incognito but crashes in your normal browser window,
            the most likely cause is stale browser app storage, a stuck service worker,
            or corrupted cached app data in that Chrome profile.
          </p>
        </div>

        <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex gap-3">
            <AlertTriangle size={22} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <h2 className="font-bold text-amber-900">Start with the safe steps</h2>
              <p className="mt-1 text-sm leading-relaxed text-amber-800">
                Clearing all site data can remove notes that only exist locally on that browser.
                If you use cloud sync and see <strong>Synced just now</strong>, your notes can be
                restored after signing in again. If you are not sure, try the cache and service
                worker steps first.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-5">
          <Card icon={Chrome} title="Confirm it is profile-specific">
            <ol className="space-y-4">
              <Step n={1} title="Open OmniCanvas in Incognito">
                In Chrome, open an Incognito window and visit{" "}
                <strong>https://app.omnicanvasnotes.com</strong>.
              </Step>
              <Step n={2} title="Check whether the app loads">
                If Incognito loads but your normal window shows <strong>Aw, Snap</strong> or{" "}
                <strong>Error code 5</strong>, your Chrome profile has stale or damaged local app data.
              </Step>
              <Step n={3} title="Do not clear data yet if you have unsynced notes">
                If you recently worked offline or are not signed in, avoid clearing all site data
                until you have confirmed your notes are synced or exported.
              </Step>
            </ol>
          </Card>

          <Card icon={RefreshCcw} title="Safe recovery: refresh cached app code">
            <ol className="space-y-4">
              <Step n={1} title="Open the normal Chrome tab">
                Use the regular browser profile that is crashing, not Incognito.
              </Step>
              <Step n={2} title="Open DevTools">
                Press <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Cmd+Option+I</kbd>{" "}
                on Mac or <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Ctrl+Shift+I</kbd>{" "}
                on Windows/Linux.
              </Step>
              <Step n={3} title="Unregister the service worker">
                Go to <strong>Application</strong> &gt; <strong>Service Workers</strong>, then click{" "}
                <strong>Unregister</strong> for OmniCanvas if a service worker is listed.
              </Step>
              <Step n={4} title="Clear Cache Storage only">
                In <strong>Application</strong> &gt; <strong>Cache Storage</strong>, delete OmniCanvas
                cache entries. This refreshes the app shell without deleting IndexedDB notes.
              </Step>
              <Step n={5} title="Hard reload">
                Reload with <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Cmd+Shift+R</kbd>{" "}
                on Mac or <kbd className="rounded bg-slate-100 px-1.5 py-0.5">Ctrl+Shift+R</kbd>{" "}
                on Windows/Linux.
              </Step>
            </ol>
          </Card>

          <Card icon={HardDrive} title="If the page still crashes">
            <ol className="space-y-4">
              <Step n={1} title="Verify your notes are synced">
                If OmniCanvas loads in Incognito after signing in and your notes are present, your
                account data is safely available from cloud sync.
              </Step>
              <Step n={2} title="Delete site data for OmniCanvas">
                In the normal Chrome profile, click the icon to the left of the address bar, open{" "}
                <strong>Site settings</strong>, then choose <strong>Delete data</strong> or{" "}
                <strong>Clear data</strong> for OmniCanvas.
              </Step>
              <Step n={3} title="Reload and sign in again">
                Return to <strong>https://app.omnicanvasnotes.com</strong>, sign in, and wait for sync
                to restore your library.
              </Step>
            </ol>
          </Card>

          <Card icon={MonitorDown} title="Installed app or desktop shell">
            <div className="space-y-3 text-sm leading-relaxed text-slate-600">
              <p>
                If the installed OmniCanvas app window shows <strong>Can't open this page</strong>,
                first open <strong>https://app.omnicanvasnotes.com</strong> in the browser profile
                that installed it and follow the steps above.
              </p>
              <p>
                For local development builds, make sure the Vite dev server is running on the URL
                configured by the desktop shell before opening the app window.
              </p>
            </div>
          </Card>

          <Card icon={Smartphone} title="Mobile browsers">
            <div className="space-y-3 text-sm leading-relaxed text-slate-600">
              <p>
                On iPhone or iPad, remove the Home Screen app shortcut, clear OmniCanvas website
                data in Safari settings, then open the site and add it back to the Home Screen.
              </p>
              <p>
                On Android Chrome, open Chrome site settings for OmniCanvas, clear storage, then
                reopen the app and sign in again.
              </p>
            </div>
          </Card>

          <Card icon={ShieldCheck} title="What OmniCanvas does automatically">
            <p className="text-sm leading-relaxed text-slate-600">
              Newer OmniCanvas releases include a boot recovery check. If the app detects that the
              previous load failed before startup completed, it attempts a one-time cleanup of app
              shell caches and service workers. It does not delete notes, folders, local IndexedDB
              data, or account data.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
